import { evaluateActionCapability } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { useCohortLifecycleMutations } from "../hooks/useCoachingQueries";
import { useParticipantLifecycleMutations } from "../hooks/useParticipantLifecycleMutations";
import { useSessionWorkflowMutations } from "../hooks/useSessionWorkflowMutations";
import { asLiveApiId, useLiveCoachingList } from "../hooks/useLiveProductData";
import type { SessionRescheduleRequest } from "../services/coaching-dtos";
import { sessionStartInstantFromWallTime } from "./sessions/session-start";
import { compactPatchBody, LiveActionControl, LiveFormModule, toOptions } from "./product-live-form-module";

/**
 * Live-mode participant, session-reschedule, and cohort-lifecycle lanes.
 * Render only in live data mode; mock/evidence fixtures stay untouched.
 */

export function LiveParticipantEditForm({ role }: { role: CoachingRole }) {
  const { update } = useParticipantLifecycleMutations();
  const participants = useLiveCoachingList("participants.list");
  const editable = participants.items.filter((participant) => participant.cohort_id);

  return (
    <LiveFormModule
      title="Edit participant"
      subtitle="PATCHes allowed fields on the cohort-scoped participant record; email stays with the identity service."
      entityLabel="participant"
      decision={evaluateActionCapability(role, "participants:update")}
      submitLabel="Save participant changes"
      successVerb="Updated"
      fields={[
        {
          name: "participantId",
          label: "Participant",
          required: true,
          options: toOptions(
            editable.map((participant) => ({
              id: participant.id,
              label: `${participant.name || participant.email || "participant"} — ${participant.cohort_name ?? `cohort ${participant.cohort_id}`}`
            }))
          ),
          optionsEmptyHint: "No live cohort-scoped participants exist yet."
        },
        { name: "name", label: "Display name", hint: "Leave blank to keep the current name." },
        { name: "phone", label: "Phone", hint: "Leave blank to keep the current phone." },
        {
          name: "status",
          label: "Status",
          options: ["invited", "active", "inactive", "completed"].map((value) => ({ value, label: value })),
          hint: "Leave unselected to keep the current status."
        }
      ]}
      onSubmit={async (values) => {
        const participant = editable.find((item) => item.id === values.participantId);
        return update.mutateAsync({
          cohortId: participant?.cohort_id ?? "",
          participantId: values.participantId,
          body: compactPatchBody(values, ["participantId"])
        });
      }}
    />
  );
}

export function LiveParticipantAttachForm({ role }: { role: CoachingRole }) {
  const { attachToCohort } = useParticipantLifecycleMutations();
  const participants = useLiveCoachingList("participants.list");
  const cohorts = useLiveCoachingList("cohorts.list");

  return (
    <LiveFormModule
      title="Attach participant to cohort"
      subtitle="Adds an existing live participant to another cohort; late joiners attach to future sessions only."
      entityLabel="participant membership"
      decision={evaluateActionCapability(role, "participants:update")}
      submitLabel="Attach participant"
      successVerb="Attached"
      span={4}
      fields={[
        {
          name: "cohortId",
          label: "Target cohort",
          required: true,
          options: toOptions(cohorts.items.map((cohort) => ({ id: cohort.id, label: cohort.name }))),
          optionsEmptyHint: "No live cohorts exist yet."
        },
        {
          name: "participantId",
          label: "Participant",
          required: true,
          options: toOptions(
            participants.items.map((participant) => ({
              id: participant.id,
              label: participant.name || participant.email || `participant ${participant.id}`
            }))
          ),
          optionsEmptyHint: "No live participants exist yet."
        }
      ]}
      onSubmit={async (values) =>
        attachToCohort.mutateAsync({
          cohortId: values.cohortId,
          body: { participantId: asLiveApiId(values.participantId) }
        })
      }
    />
  );
}

export function LiveSessionRescheduleForm({ role, sessionId }: { role: CoachingRole; sessionId: string | null }) {
  const { rescheduleOne } = useSessionWorkflowMutations();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <LiveFormModule
      title="Reschedule this session live"
      subtitle={`One-off reschedule keeps the same session id${sessionId ? ` (session ${sessionId})` : ""}; past sessions stay unchanged.`}
      entityLabel="session"
      decision={evaluateActionCapability(role, "sessions:reschedule")}
      submitLabel="Reschedule session"
      successVerb="Rescheduled"
      fields={[
        { name: "newDate", label: "New date", type: "date", required: true },
        { name: "newTime", label: "New start time", type: "time", required: true },
        { name: "newTimezone", label: "Timezone", hint: `Leave blank to use ${browserTimezone}.` },
        { name: "notes", label: "Reschedule notes" }
      ]}
      validate={(values): Record<string, string> => {
        const timezone = values.newTimezone || browserTimezone;
        const newStart = sessionStartInstantFromWallTime(values.newDate, values.newTime, timezone);
        // An unparseable timezone is the server's call; only a computed past start is blocked here.
        if (!newStart) return {};
        return newStart.getTime() <= Date.now() ? { newDate: "Choose a new start in the future." } : {};
      }}
      onSubmit={async (values) => {
        const timezone = values.newTimezone || browserTimezone;
        // Canonical one-off reschedule keys (validators.rescheduleSession) —
        // the 2026-07-05 walk retired the old dual-shape body.
        const body: SessionRescheduleRequest = {
          new_date: values.newDate,
          new_time: values.newTime,
          new_timezone: timezone,
          ...(values.notes ? { notes: values.notes } : {})
        };
        return rescheduleOne.mutateAsync({ sessionId: sessionId ?? "", body });
      }}
    />
  );
}

export function LiveCohortCompleteAction({ role, cohortId }: { role: CoachingRole; cohortId: string | null }) {
  const { complete } = useCohortLifecycleMutations();

  return (
    <LiveActionControl
      label="Complete cohort"
      confirmLabel="Confirm cohort completion"
      entityLabel="cohort"
      successVerb="Completed"
      decision={evaluateActionCapability(role, "cohorts:complete")}
      onConfirm={() => complete.mutateAsync(cohortId ?? "")}
    />
  );
}

export function LiveAssignMembershipAction({ role, cohortId }: { role: CoachingRole; cohortId: string | null }) {
  const { assignMembership } = useParticipantLifecycleMutations();

  return (
    <LiveActionControl
      label="Assign cohort membership"
      confirmLabel="Confirm membership assignment"
      entityLabel="cohort membership"
      successVerb="Assigned"
      decision={evaluateActionCapability(role, "participants:update")}
      onConfirm={() => assignMembership.mutateAsync({ cohortId: cohortId ?? "" })}
    />
  );
}
